import React from "react"; // For JSX
import Image from "next/image"; // Import Next.js Image component
import Link from "next/link";
import { routes } from "@/routes";
import { Button } from "@/components/ui/button"; // For potential use in skeletons
import { Container } from "@/components/ui/container";

// Placeholder Icon Components (replace with actual icons)
const IconStripe = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);
const IconNotification = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);
const IconCalendarCheck = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zM7 15h0m4 0h0m4 0h0"
    />
  </svg>
);
const IconDashboard = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

// New Checkmark Icon
const IconCheck = () => (
  <svg
    className="h-5 w-5 text-green-500 mr-2 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

// Updated SkeletonStripeConnect
const SkeletonStripeConnect = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50">
    <div className="flex items-center mb-2">
      <svg
        className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z" />
        <path d="M14 6H6v2h8V6zm0 4H6v2h8v-2z" />
      </svg>
      <h4 className="text-sm font-medium text-foreground/80">
        Example: League Sales (Optional via Stripe)
      </h4>
    </div>
    <div className="space-y-2">
      {/* Event Ticket Example */}
      <div className="p-2 rounded-md bg-background/70 border">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs font-medium text-foreground/90">
              EOY Tournament Ticket
            </p>
            <p className="text-xs text-muted-foreground">
              Entry for one player
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-primary">$25.00</p>
            <Link href={routes.register}>
              <Button
                variant="outline"
                size="sm"
                className="text-xs mt-1 h-7 px-2 py-1"
              >
                Buy Ticket
              </Button>
            </Link>
          </div>
        </div>
      </div>
      {/* Merchandise Example */}
      <div className="p-2 rounded-md bg-background/70 border">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs font-medium text-foreground/90">
              League Performance Polo
            </p>
            <p className="text-xs text-muted-foreground">Sizes: S, M, L, XL</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-primary">$30.00</p>
            <Link href={routes.register}>
              <Button
                variant="outline"
                size="sm"
                className="text-xs mt-1 h-7 px-2 py-1"
              >
                Add to Cart
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
    <p className="text-xs text-muted-foreground mt-2 text-center">
      Manage items and accept payments directly.
    </p>
  </div>
);

// New SkeletonCommunicationFeaturesPreview
const SkeletonCommunicationFeaturesPreview = () => (
  <div className="mt-4 space-y-3">
    {/* League Chat Mockup */}
    <div className="p-3 border border-dashed rounded-md bg-muted/50">
      <h4 className="text-sm font-medium text-foreground/80 mb-2">
        League Chat
      </h4>
      <div className="space-y-2 text-xs">
        <div className="flex items-start space-x-1.5">
          <span className="font-semibold text-primary/90">Admin:</span>
          <span className="text-muted-foreground">
            Next week's meetup is at Willow Creek, 9 AM!
          </span>
        </div>
        <div className="flex items-start space-x-1.5">
          <span className="font-semibold text-green-600">PlayerA:</span>
          <span className="text-muted-foreground">Sounds good, I'm in.</span>
        </div>
        <div className="flex items-start space-x-1.5">
          <span className="font-semibold text-blue-600">PlayerB:</span>
          <span className="text-muted-foreground">Can I bring a guest?</span>
        </div>
      </div>
    </div>
    {/* League Feed Mockup */}
    <div className="p-3 border border-dashed rounded-md bg-muted/50">
      <h4 className="text-sm font-medium text-foreground/80 mb-2">
        League Activity Feed
      </h4>
      <div className="space-y-1.5 text-xs">
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground/80">🏆 Player X</span>{" "}
          defeated{" "}
          <span className="font-semibold text-foreground/80">Player Y</span>{" "}
          (3&2) in Match Play at <span className="italic">Pinehurst No. 2</span>
          .
        </p>
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground/80">
            🎉 New Event:
          </span>{" "}
          Summer Scramble announced for July 15th.
        </p>
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground/80">📈 Player Z</span>{" "}
          reached Gold Tier MMR!
        </p>
      </div>
    </div>
  </div>
);

const SkeletonCheckInPreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50">
    <h4 className="text-sm font-medium text-foreground/80 mb-2">
      Event Check-in Status
    </h4>
    <div className="space-y-1">
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">Player Alice</span>
        <span className="text-green-600 font-medium">Checked In</span>
      </div>
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">Player Bob</span>
        <span className="text-yellow-600 font-medium">Pending</span>
      </div>
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground">Player Charlie</span>
        <span className="text-green-600 font-medium">Checked In</span>
      </div>
    </div>
  </div>
);

// Updated SkeletonDashboardWidgetPreview
const SkeletonDashboardWidgetPreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50 space-y-3">
    <h4 className="text-sm font-medium text-foreground/80 mb-2 text-center">
      League Dashboard Overview
    </h4>
    {/* Stats Section */}
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-background p-2 rounded text-center shadow">
        <p className="text-xs text-muted-foreground">Active Members</p>
        <p className="text-lg font-bold text-primary">57</p>
      </div>
      <div className="bg-background p-2 rounded text-center shadow">
        <p className="text-xs text-muted-foreground">Events Today</p>
        <p className="text-lg font-bold text-primary">3</p>
      </div>
    </div>
    {/* Quick Actions Section */}
    <div className="p-2 rounded-md bg-background/70 border">
      <h5 className="text-xs font-semibold text-foreground/70 mb-1.5 text-center">
        Quick Actions
      </h5>
      <Link href={routes.register} className="w-full">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs h-7 px-2 py-1"
        >
          📅 Schedule New Event
        </Button>
      </Link>
    </div>
    {/* AI Pairings Section */}
    <div className="p-2 rounded-md bg-background/70 border">
      <h5 className="text-xs font-semibold text-foreground/70 mb-1.5 text-center">
        ✨ AI Pairing Options
      </h5>
      <ul className="space-y-1 text-xs text-muted-foreground">
        <li className="flex items-center justify-center">
          <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full text-[10px]">
            New Connections
          </span>
        </li>
        <li className="flex items-center justify-center">
          <span className="bg-green-500/10 text-green-600 px-1.5 py-0.5 rounded-full text-[10px]">
            Similar Styles
          </span>
        </li>
        <li className="flex items-center justify-center">
          <span className="bg-blue-500/10 text-blue-600 px-1.5 py-0.5 rounded-full text-[10px]">
            Skill-Matched
          </span>
        </li>
      </ul>
      <p className="text-[10px] text-muted-foreground/80 mt-1.5 text-center">
        Manager Configurable
      </p>
    </div>
  </div>
);

export const LeaguesSection = () => {
  return (
    <section
      id="leagues-section"
      className="w-full py-12 md:py-24 lg:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg3.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center 40%",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Light overlay for readability */}
      <div className="absolute inset-0 bg-muted/90" />
      <Container className="relative z-10">
        <div className="text-center mb-12 md:mb-16 px-4 md:px-0">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm mb-4">
            <span>League Manager Role • Separate from Member subscription</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            For Leagues: Rally Your Locals, Own the Leaderboard
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
            Empower your golf league with Local Links. We provide the tools to
            manage, monetize, and engage your community, so you can focus on
            fostering great competition and camaraderie.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
          {/* Feature 1: Monetize */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                {" "}
                {/* Added pt-1 for alignment */}
                <IconStripe />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Monetize Your League</h3>
                <p className="text-muted-foreground mb-3">
                  Optionally turn your passion into profit. Securely collect
                  membership dues, sell event tickets, or offer league
                  merchandise through our Stripe integration. Fund your events,
                  offer prizes, and grow your league's offerings.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Collect profits directly to fund league activities.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Offer easy payment options for your members.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Grow your league with a sustainable funding model.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              {" "}
              {/* Ensures skeleton is at the bottom */}
              <SkeletonStripeConnect />
            </div>
          </div>
          {/* Feature 2: Communication */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconNotification />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  Effortless Communication
                </h3>
                <p className="text-muted-foreground mb-3">
                  Engage your members directly. Send updates, announce events,
                  and notify players with real-time alerts. Keep the
                  conversation flowing with integrated chat and an activity
                  feed.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Keep everyone informed and connected.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Build a stronger, more active league presence.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Centralize league discussions and updates.</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonCommunicationFeaturesPreview />
            </div>
          </div>
          {/* Feature 3: Event Management */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconCalendarCheck />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  Streamlined Event Management
                </h3>
                <p className="text-muted-foreground mb-3">
                  Simplify event days with easy player check-ins, faster
                  tournament kickoffs, and optional real-time match data. Plus,
                  send quick mobile notifications for important updates.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Reduce pre-tournament admin and delays.</span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Enhance the competitive experience with live data.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>Keep events running smoothly and efficiently.</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4 space-y-4">
              {" "}
              {/* Added space-y-4 for stacking */}
              <SkeletonCheckInPreview />
              <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[8px] rounded-[2.5rem] h-[300px] w-[150px] shadow-xl">
                <div className="w-[70px] h-[10px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>
                <div className="h-[20px] w-[2px] bg-gray-800 absolute -start-[10px] top-[50px] rounded-s-lg"></div>
                <div className="h-[30px] w-[2px] bg-gray-800 absolute -start-[10px] top-[80px] rounded-s-lg"></div>
                <div className="h-[30px] w-[2px] bg-gray-800 absolute -end-[10px] top-[100px] rounded-e-lg"></div>
                <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white dark:bg-gray-800">
                  <Image
                    src="/landing-page-iphone-example.png"
                    alt="iPhone showing Local Links event notification example"
                    width={134}
                    height={284}
                    className="dark:hidden w-full h-full object-cover"
                  />
                  <Image
                    src="/landing-page-iphone-example.png" // Assuming same image for dark mode
                    alt="iPhone showing Local Links event notification example (dark mode)"
                    width={134}
                    height={284}
                    className="hidden dark:block w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Feature 4: League Dashboard */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconDashboard />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  Powerful League Dashboard
                </h3>
                <p className="text-muted-foreground mb-3">
                  Your central command for all things league-related.
                  Effortlessly manage league settings, customize rules and
                  competition formats, oversee member applications and roles,
                  schedule events, track scores, and dive into detailed
                  statistics and analytics. Integrated course management allows
                  you to define approved courses and specific rules for your
                  league play. It's all here to help you run your league
                  smoothly.{" "}
                  <span className="block mt-2 text-primary font-medium">
                    Subscribers unlock advanced reporting, data export, and
                    extended data history to truly optimize league performance
                    and member engagement.
                  </span>
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Get insights into league activity and participation.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Track finances and manage memberships with ease.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Utilize smart tools for event setup and pairings.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonDashboardWidgetPreview />
            </div>
          </div>
        </div>
        {/* Optional Call to Action
        <div className="text-center mt-12 md:mt-16">
          <Button size="lg" className="px-8 py-3 text-lg">
            Start Your League Today
          </Button>
        </div>
        */}
      </Container>
    </section>
  );
};
